import React, { useMemo, useState } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

function formatDateRangeLabel(range) {
  const start = range?.[0];
  const end = range?.[1];
  if (!start?.isValid?.() || !end?.isValid?.()) return '—';
  if (start.isSame(end, 'day')) return start.format('DD/MM/YY');
  return `${start.format('DD/MM/YY')} – ${end.format('DD/MM/YY')}`;
}

/**
 * Compact "Date range" filter: closed state shows the selected range; open menu shows
 * start/end pickers and dual-handle slider stacked vertically.
 */
function DateRangeFilter({
  availableDates = [],
  effectiveDateRange,
  dateRange,
  setDateRange,
  setPreviewDateRange,
  dateSliderValue,
  onSliderChange,
  onSliderCommit,
  accentColor = '#2e7d32',
  minWidth = 160,
  fullWidth = false,
  sx,
}) {
  const [open, setOpen] = useState(false);
  const label = useMemo(() => formatDateRangeLabel(effectiveDateRange), [effectiveDateRange]);

  if (!availableDates.length) return null;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <FormControl size="small" fullWidth={fullWidth} sx={{ minWidth: fullWidth ? undefined : minWidth, ...sx }}>
        <InputLabel id="date-range-filter-label">Date range</InputLabel>
        <Select
          labelId="date-range-filter-label"
          label="Date range"
          value="date-range-panel"
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          renderValue={() => label}
          MenuProps={{
            PaperProps: { sx: { width: 300, maxWidth: '95vw' } },
            autoFocus: false,
          }}
        >
          <MenuItem value="date-range-panel" sx={{ display: 'none' }} />
          <Box
            component="li"
            sx={{
              listStyle: 'none',
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              px: 2,
              py: 1.5,
            }}
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => e.stopPropagation()}
          >
            <DatePicker
              label="Start Date"
              value={effectiveDateRange[0]}
              minDate={availableDates[0]}
              maxDate={effectiveDateRange[1] || availableDates[availableDates.length - 1]}
              onChange={(newValue) => {
                setPreviewDateRange(null);
                setDateRange([newValue, dateRange[1]]);
              }}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  sx: { '& .MuiInputBase-root': { height: 36 } },
                },
              }}
            />
            <DatePicker
              label="End Date"
              value={effectiveDateRange[1]}
              minDate={effectiveDateRange[0] || availableDates[0]}
              maxDate={availableDates[availableDates.length - 1]}
              onChange={(newValue) => {
                setPreviewDateRange(null);
                setDateRange([dateRange[0], newValue]);
              }}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  sx: { '& .MuiInputBase-root': { height: 36 } },
                },
              }}
            />
            <Slider
              value={Array.isArray(dateSliderValue) ? dateSliderValue : [dateSliderValue, dateSliderValue]}
              onChange={onSliderChange}
              onChangeCommitted={onSliderCommit}
              min={0}
              max={Math.max(availableDates.length - 1, 0)}
              step={1}
              disableSwap
              valueLabelDisplay="off"
              sx={{
                px: 0.5,
                color: accentColor,
                '& .MuiSlider-track': { backgroundColor: accentColor },
                '& .MuiSlider-rail': { backgroundColor: `${accentColor}40` },
                '& .MuiSlider-thumb': { borderColor: accentColor },
              }}
            />
          </Box>
        </Select>
      </FormControl>
    </LocalizationProvider>
  );
}

export default DateRangeFilter;
